import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap, take } from 'rxjs';
import { Store } from '@ngxs/store';
import { WooCommerceShippingAddress } from '../../interfaces/woo-commerce/api/woo-commerce-api.interface';
import { ConfirmDialogComponent } from '../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { HappyStoreSelectionService } from './happy-store-selection.service';
import { ClearRestaurantAction } from '../../../shared/state/restaurant/restaurant.actions';
import { CartItemsAction } from '../../../shared/state/cart/cart.actions';
import {
  SetAvailableShippingMethodsAction,
  UpsertShippingAction
} from '../../../shared/state/shipping/shipping.actions';
import { SetDeliveryAddresses } from '../../../shared/state/delivery/delivery.actions';

export type HappyStoreSwitchResult = 'continue' | 'switching' | 'cancelled';

@Injectable({ providedIn: 'root' })
export class HappyStoreSwitchService {
  private readonly dialog = inject(Dialog);
  private readonly store = inject(Store);
  private readonly happyStoreSelection = inject(HappyStoreSelectionService);

  confirmPickupLocationStore(
    address: WooCommerceShippingAddress
  ): Observable<HappyStoreSwitchResult> {
    if (!this.requiresStoreSwitch(address)) {
      return of<HappyStoreSwitchResult>('continue');
    }

    const dialogRef: DialogRef<boolean, ConfirmDialogComponent> = this.dialog.open(
      ConfirmDialogComponent,
      {
        data: {
          title: 'Switch pickup location?',
          message:
            'Products in this location are different. We will switch you to that store and load its own cart, login, and saved checkout details.',
          cancelLabel: 'Cancel',
          confirmLabel: 'Switch location'
        },
        width: '100%',
        maxWidth: '550px'
      }
    );

    return dialogRef.closed.pipe(
      take(1),
      switchMap((confirmed: boolean | undefined) => {
        if (!confirmed) {
          return of<HappyStoreSwitchResult>('cancelled');
        }

        return of(this.switchToStoreIndex(address.storeIndex ?? -1));
      })
    );
  }

  switchToStoreIndex(storeIndex: number): HappyStoreSwitchResult {
    if (storeIndex < 0) {
      return 'cancelled';
    }

    const selectionChanged = this.happyStoreSelection.select(storeIndex);
    if (!selectionChanged) {
      return 'continue';
    }

    this.clearStateBeforeStoreSwitch();
    this.reloadCurrentUrl();
    return 'switching';
  }

  getSelectedStoreApiKey(): string | null {
    return this.happyStoreSelection.getSelected()?.storeApiKey || null;
  }

  private requiresStoreSwitch(address: WooCommerceShippingAddress): boolean {
    if (address.storeIndex == null) {
      return false;
    }

    const selectedStore = this.happyStoreSelection.getSelected();
    if (!selectedStore) {
      return false;
    }

    return (
      selectedStore.storeApiKey !== address.storeApiKey ||
      selectedStore.tenant !== address.tenant
    );
  }

  private reloadCurrentUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const nextUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(nextUrl);
  }

  private clearStateBeforeStoreSwitch(): void {
    this.store.dispatch([
      new ClearRestaurantAction(),
      new CartItemsAction.Clear(),
      new UpsertShippingAction({
        address: undefined,
        method: undefined,
        methodDetails: undefined,
        schedule: undefined
      }),
      new SetAvailableShippingMethodsAction({
        availableShippingMethods: [],
        selectedShippingMethod: undefined
      }),
      new SetDeliveryAddresses([])
    ]);
  }
}
